import { memo, useCallback, useMemo } from 'react';
import {
  createDatePresetsSection,
  createMultiSelectSection,
  createSingleSelectSection,
  FilterSlideout,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { ADDED_FILTER_OPTIONS, MEMBER_QUICK_FILTERS, MFA_FILTER } from '../../constants/membersFilters';

/** Value of the "All" radio of the Status section. */
const STATUS_ALL = 'all';

/**
 * "All filters" slideout of the Members list. Edits the draft only: the list changes when the admin
 * applies; closing without Apply discards. Single-selects (MFA) read and write the scalar directly.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {{ status: string, role: string[], group: string[], mfa: string, added: string, importBatch: string }} props.draft
 * @param {(key: string, value: string|string[]) => void} props.setDraftField
 * @param {() => void} props.applyDraft
 * @param {() => void} props.clearDraft
 * @param {{ roles: object[], groups: object[], batches: object[] }} props.options
 */
function MembersFiltersSlideout({
  isOpen,
  onClose,
  draft,
  setDraftField,
  applyDraft,
  clearDraft,
  options,
}) {
  const { t } = useTranslation('people-access');

  const sections = useMemo(
    () => [
      createSingleSelectSection({
        id: 'status',
        title: t('members.filters.statusSection'),
        // "All" is a real radio: the section needs a non-empty value to show it checked, while the
        // filter state keeps '' for "no status".
        value: draft.status || STATUS_ALL,
        allValue: STATUS_ALL,
        options: MEMBER_QUICK_FILTERS.map(({ value, labelKey }) => ({
          value: value || STATUS_ALL,
          label: t(labelKey),
        })),
        onChange: (next) => setDraftField('status', next === STATUS_ALL ? '' : next),
      }),
      createMultiSelectSection({
        id: 'role',
        title: t('members.filters.role'),
        value: draft.role,
        options: options.roles,
        onChange: (next) => setDraftField('role', next),
      }),
      createMultiSelectSection({
        id: 'group',
        title: t('members.filters.group'),
        value: draft.group,
        options: options.groups,
        onChange: (next) => setDraftField('group', next),
      }),
      createSingleSelectSection({
        id: 'mfa',
        title: t('members.filters.mfaSection'),
        value: draft.mfa,
        allValue: '',
        options: [
          { value: MFA_FILTER.ON, label: t('members.mfa.on') },
          { value: MFA_FILTER.OFF, label: t('members.mfa.off') },
        ],
        onChange: (next) => setDraftField('mfa', next),
      }),
      createDatePresetsSection({
        id: 'added',
        title: t('members.columns.added'),
        value: draft.added,
        allValue: '',
        options: ADDED_FILTER_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) })),
        onChange: (next) => setDraftField('added', next),
      }),
      ...(options.batches.length
        ? [
            createSingleSelectSection({
              id: 'importBatch',
              title: t('members.filters.imported'),
              value: draft.importBatch,
              allValue: '',
              options: options.batches,
              onChange: (next) => setDraftField('importBatch', next),
            }),
          ]
        : []),
    ],
    [t, draft, options, setDraftField],
  );

  // The slideout's own buttons default to English; give it our copy.
  const slideoutI18n = useMemo(
    () => ({
      close: t('members.filters.slideoutClose'),
      clearAll: t('members.filters.slideoutClear'),
      apply: t('members.filters.slideoutApply'),
    }),
    [t],
  );

  const handleApply = useCallback(() => {
    applyDraft();
    onClose();
  }, [applyDraft, onClose]);

  return (
    <FilterSlideout
      isOpen={isOpen}
      onClose={onClose}
      title={t('members.filters.title')}
      i18n={slideoutI18n}
      sections={sections}
      onClearAll={clearDraft}
      onApply={handleApply}
    />
  );
}

export default memo(MembersFiltersSlideout);
